import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { CalendarBlank, X } from "@phosphor-icons/react";
import { getDateFilter, setDateFilterLocal, clearDateFilterLocal } from "@/lib/fyRange";

const fmtD = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return `${d.getDate()}-${d.toLocaleString("en-GB", { month: "short" })}-${d.getFullYear()}`;
  } catch { return iso; }
};

// Global date filter: F2 = single date popup, Alt+F2 = date range popup.
// Accept applies the filter software-wide (all screens) and reloads in place.
export function DateFilterPopup() {
  const location = useLocation();
  const [, forceRender] = useState(0);
  const active = getDateFilter(); // null when set on a different screen
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState(""); // "" | "single" | "range"
  const [d1, setD1] = useState(active ? active.from : today);
  const [d2, setD2] = useState(active ? active.to : today);
  const firstRef = useRef(null);

  useEffect(() => {
    const f = () => forceRender((v) => v + 1);
    window.addEventListener("datefilter-changed", f);
    return () => window.removeEventListener("datefilter-changed", f);
  }, []);

  // Screen-scoped: leaving the screen where the filter was set drops it,
  // so every other screen shows normal data.
  useEffect(() => {
    const df = getDateFilter();
    if (!df) clearDateFilterLocal();
  }, [location.pathname]);

  const accept = () => {
    if (!d1) return;
    if (mode === "single") setDateFilterLocal(d1, d1);
    else {
      if (!d2) return;
      const f = d1 <= d2 ? d1 : d2;
      const t = d1 <= d2 ? d2 : d1;
      setDateFilterLocal(f, t);
    }
    setMode("");
    // No page reload — the current screen refetches its data in place.
    window.dispatchEvent(new Event("datefilter-changed"));
  };
  const clear = () => {
    clearDateFilterLocal();
    setMode("");
    window.dispatchEvent(new Event("datefilter-changed"));
  };

  // Stable, FIRST-registered capture listener (Layout mounts before any view),
  // uses stopImmediatePropagation so screen-level Esc handlers never fire when
  // the popup or an active filter consumes the key.
  const ref = useRef({});
  ref.current = { mode, active, today, accept, clear };
  useEffect(() => {
    const onKey = (e) => {
      if (window.location.pathname.startsWith("/ledger-views")) return;
      const s = ref.current;
      if (e.key === "F2") {
        e.preventDefault(); e.stopImmediatePropagation();
        setD1(s.active ? s.active.from : s.today);
        setD2(s.active ? s.active.to : s.today);
        setMode(e.altKey ? "range" : "single");
        return;
      }
      if (s.mode && e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); setMode(""); }
      else if (s.mode && e.key === "Enter") { e.preventDefault(); e.stopImmediatePropagation(); s.accept(); }
      // Filter active + Esc (popup closed) -> ONLY clear the filter and stay
      // on this exact screen/sub-screen with full data.
      else if (!s.mode && e.key === "Escape" && getDateFilter()) {
        e.preventDefault(); e.stopImmediatePropagation(); s.clear();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  useEffect(() => {
    if (mode && firstRef.current) setTimeout(() => firstRef.current && firstRef.current.focus(), 50);
  }, [mode]);

  return (
    <>
      {active && (
        <div className="fixed top-1 left-1/2 -translate-x-1/2 z-[90] flex items-center gap-2 px-3 py-1 rounded-full shadow-lg text-white text-xs font-bold"
          style={{ background: "#0f766e" }} data-testid="date-filter-badge">
          <CalendarBlank size={13} weight="bold" />
          <span data-testid="date-filter-badge-label">
            {active.from === active.to ? fmtD(active.from) : `${fmtD(active.from)} \u2192 ${fmtD(active.to)}`}
          </span>
          <span className="opacity-70 font-semibold hidden sm:inline">· F2 change</span>
          <button onClick={clear} data-testid="date-filter-clear-badge" title="Clear date filter"
            className="p-0.5 hover:bg-white/25 rounded-full"><X size={12} weight="bold" /></button>
        </div>
      )}

      {mode && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          data-testid="date-filter-modal" onClick={() => setMode("")}>
          <div className="w-full max-w-sm rounded-md overflow-hidden shadow-2xl" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="text-white text-center py-2 text-sm font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>
              {mode === "single" ? "Date Filter" : "Date Range Filter"}
            </div>
            <div className="px-8 py-6 space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                  {mode === "single" ? "Date" : "From Date"}
                </label>
                <input ref={firstRef} type="date" value={d1} onChange={(e) => setD1(e.target.value)}
                  data-testid="date-filter-from"
                  className="w-full px-3 py-2 border border-slate-400 rounded-sm text-sm font-semibold bg-white" />
              </div>
              {mode === "range" && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">To Date</label>
                  <input type="date" value={d2} onChange={(e) => setD2(e.target.value)}
                    data-testid="date-filter-to"
                    className="w-full px-3 py-2 border border-slate-400 rounded-sm text-sm font-semibold bg-white" />
                </div>
              )}
              <button onClick={accept} data-testid="date-filter-accept"
                className="w-full py-2.5 text-white text-sm font-black rounded-sm hover:opacity-90 transition-opacity"
                style={{ background: "#0f766e" }}>
                Accept
              </button>
              {active && (
                <button onClick={clear} data-testid="date-filter-clear"
                  className="w-full py-2 text-xs font-bold text-red-700 border border-red-300 rounded-sm hover:bg-red-50">
                  Clear Date Filter
                </button>
              )}
              <div className="text-[10px] text-slate-500 text-center font-semibold">
                Enter Accept · Esc Cancel · Applies to every screen
              </div>
            </div>
            <div className="h-1.5" style={{ background: "#0f766e" }} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
